import React from "react";
import {
  FileSpreadsheet,
  FileText,
  Image as ImageIcon,
  Loader2,
  Upload,
} from "lucide-react";
import { UploadedFileMeta } from "@amzn/afxtaco-types";
import { formatFileSize, formatRelativeTime } from "@/utils/formatTime";
import OverviewCard from "./OverviewCard";
import EmptyState from "./EmptyState";
import FileRow from "./FileRow";

interface UploadedFilesCardProps {
  files: UploadedFileMeta[];
  isLoading: boolean;
}

const IMAGE_EXTENSIONS = new Set(["png", "jpg", "jpeg"]);
const SHEET_EXTENSIONS = new Set(["xls", "xlsx", "csv"]);

const iconFor = (extension: string): React.ReactNode => {
  if (IMAGE_EXTENSIONS.has(extension)) return <ImageIcon className="h-4 w-4" />;
  if (SHEET_EXTENSIONS.has(extension))
    return <FileSpreadsheet className="h-4 w-4" />;
  return <FileText className="h-4 w-4" />;
};

/**
 * The files the user attached to this project.
 *
 * Read-only. Uploads are excluded from every other listing path and can't be
 * downloaded back today, so this is the only surface that shows the full set —
 * and the only one that survives a reload, since the in-chat file chips are held
 * in memory.
 */
const UploadedFilesCard: React.FC<UploadedFilesCardProps> = ({
  files,
  isLoading,
}) => (
  <OverviewCard
    icon={<Upload className="h-3.5 w-3.5" />}
    title="Files you uploaded"
    count={isLoading ? undefined : files.length}
  >
    {isLoading ? (
      <div className="flex items-center justify-center gap-2 py-8">
        <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
        <span className="text-xs text-muted-foreground">Loading files...</span>
      </div>
    ) : files.length === 0 ? (
      <EmptyState>
        Attach a baseline diagram, an RFP, or a legacy SOW and TACO will read it
        as context.
      </EmptyState>
    ) : (
      files.map((file) => {
        const size = formatFileSize(file.sizeBytes);
        const when = file.lastModified
          ? formatRelativeTime(file.lastModified)
          : "";
        return (
          <FileRow
            key={file.path}
            icon={iconFor(file.extension)}
            label={file.filename}
            sub={[size, when].filter(Boolean).join(" · ")}
          />
        );
      })
    )}
  </OverviewCard>
);

export default UploadedFilesCard;
